<template>
  <div class="container mx-auto px-4 py-8">
    <div class="flex justify-between items-center mb-6">
      <h1 class="text-2xl font-bold text-gray-900 dark:text-white">Products</h1>
      <button @click="openAddProduct" class="btn-primary">Add Product</button>
    </div>

    <DataTable
      :columns="columns"
      resourceName="products"
      @rowClick="showProductDetails"
    >
      <template #cell="{ column, value }">
        <template v-if="column.key === 'description'">
          <span :title="value">{{ truncate(value, 80) }}</span>
        </template>
      </template>
    </DataTable>

    <!-- Product Modal -->
    <div v-if="showModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" @click.self="closeModal">
      <div class="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] flex flex-col">
        <div class="p-6 pb-0 flex-shrink-0">
          <div class="flex justify-between items-start mb-4">
            <h2 class="text-xl font-semibold text-gray-900 dark:text-white">
              {{ selectedProduct ? selectedProduct.name : 'Add Product' }}
            </h2>
            <button @click="closeModal" class="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300">
              <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>

          <div v-if="selectedProduct" class="flex border-b border-gray-200 dark:border-gray-700 overflow-x-auto">
            <button v-for="t in modalTabs" :key="t.id" @click="activeTab = t.id"
              class="px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px whitespace-nowrap"
              :class="activeTab === t.id ? 'border-blue-500 text-blue-600 dark:text-blue-400' : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700'">
              {{ t.label }}
              <span v-if="t.count !== undefined && t.count > 0" class="ml-1.5 inline-flex items-center justify-center w-5 h-5 text-xs font-semibold rounded-full bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-300">{{ t.count }}</span>
            </button>
          </div>
        </div>

        <div class="p-6 overflow-y-auto flex-1">
          <!-- Details Tab -->
          <div v-show="activeTab === 'details'">
            <form @submit.prevent="handleSubmit" class="space-y-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Name</label>
                <input v-model="form.name" type="text" required class="input mt-1" />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Description</label>
                <textarea v-model="form.description" rows="3" class="input mt-1"></textarea>
              </div>
              <div class="grid grid-cols-2 gap-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Website</label>
                  <input v-model="form.website" type="url" placeholder="https://" class="input mt-1" />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Security Contact</label>
                  <input v-model="form.security_contact" type="text" class="input mt-1" />
                </div>
              </div>

              <!-- SBOMs / Scan Reports visibility -->
              <div class="rounded-lg border border-gray-200 dark:border-gray-600 p-4">
                <label class="flex items-start gap-3 cursor-pointer">
                  <div class="relative flex-shrink-0 mt-0.5">
                    <input type="checkbox" v-model="form.sboms_public" class="sr-only peer" />
                    <div class="w-9 h-5 rounded-full transition-colors bg-gray-200 dark:bg-gray-600 peer-checked:bg-blue-600 dark:peer-checked:bg-blue-500"></div>
                    <div class="absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform peer-checked:translate-x-4"></div>
                  </div>
                  <div>
                    <span class="block text-sm font-medium text-gray-900 dark:text-white">SBOMs &amp; Scan Reports are public</span>
                    <span class="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      When off, all SBOMs and scan reports for this product are restricted. Customers see a locked indicator and can request access via a Customer Access grant. The EU Declaration of Conformity document is unaffected.
                    </span>
                  </div>
                </label>
              </div>
              <div class="flex justify-end space-x-3 mt-6">
                <button v-if="selectedProduct" type="button" @click="deleteProduct(selectedProduct)" :disabled="isSaving" class="px-4 py-2 border border-red-300 dark:border-red-600 rounded-md text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20">Delete</button>
                <button type="button" @click="closeModal" :disabled="isSaving" class="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">Cancel</button>
                <button type="submit" :disabled="isSaving" class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50">
                  {{ isSaving ? 'Saving...' : (selectedProduct ? 'Save Changes' : 'Add Product') }}
                </button>
              </div>
            </form>
          </div>

          <!-- Versions Tab -->
          <div v-show="activeTab === 'versions'">
            <div class="flex justify-between items-center mb-4">
              <div class="flex items-center gap-3">
                <p class="text-sm text-gray-500 dark:text-gray-400">{{ versions.length }} version{{ versions.length !== 1 ? 's' : '' }}</p>
                <input v-if="versions.length > 10" v-model="versionSearch" type="text" placeholder="Search versions..."
                  class="input text-sm" />
              </div>
              <button @click="openAddVersion" class="btn-primary text-sm">Add Version</button>
            </div>

            <div v-if="filteredVersions.length === 0" class="text-center py-10 border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-lg">
              <p class="text-gray-500 dark:text-gray-400 text-sm">{{ versionSearch ? 'No versions match your search.' : 'No versions yet.' }}</p>
            </div>

            <div v-else class="space-y-2 max-h-[400px] overflow-y-auto">
              <div v-for="ver in paginatedVersions" :key="ver.id"
                class="card px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50"
                @click="openVersionDetail(ver)">
                <div class="flex items-center gap-3">
                  <span class="inline-flex items-center px-2.5 py-1 rounded text-sm font-semibold bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">v{{ ver.version }}</span>
                  <div>
                    <p v-if="ver.support_end_date" class="text-xs text-gray-500 dark:text-gray-400">Support ends {{ formatDate(ver.support_end_date) }}</p>
                    <p v-if="ver.release_notes" class="text-xs text-gray-400 dark:text-gray-500 truncate max-w-xs">{{ truncate(stripHtml(ver.release_notes), 60) }}</p>
                  </div>
                </div>
                <div class="flex items-center gap-3">
                  <!-- Per-version severity chips, populated lazily after fetchVersionMetrics -->
                  <div v-if="versionMetrics[ver.id]" class="flex items-center gap-1 text-[11px]">
                    <span v-if="versionMetrics[ver.id].kev > 0"
                          class="px-1.5 py-0.5 rounded bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 font-bold"
                          title="Actively-exploited findings">KEV {{ versionMetrics[ver.id].kev }}</span>
                    <span v-if="versionMetrics[ver.id].critical > 0"
                          class="px-1.5 py-0.5 rounded bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300">{{ versionMetrics[ver.id].critical }} C</span>
                    <span v-if="versionMetrics[ver.id].high > 0"
                          class="px-1.5 py-0.5 rounded bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300">{{ versionMetrics[ver.id].high }} H</span>
                    <span v-if="versionMetrics[ver.id].medium > 0"
                          class="px-1.5 py-0.5 rounded bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-300">{{ versionMetrics[ver.id].medium }} M</span>
                    <span v-if="versionMetrics[ver.id].low > 0"
                          class="px-1.5 py-0.5 rounded bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300">{{ versionMetrics[ver.id].low }} L</span>
                    <span v-if="versionMetrics[ver.id].risk_score > 0"
                          class="ml-1 text-gray-500 dark:text-gray-400 font-mono">risk {{ versionMetrics[ver.id].risk_score }}</span>
                  </div>
                  <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/></svg>
                </div>
              </div>

              <div v-if="filteredVersions.length > versionPageSize" class="flex items-center justify-center gap-2 pt-3">
                <button @click="versionPage = Math.max(1, versionPage - 1)" :disabled="versionPage <= 1"
                  class="px-3 py-1.5 text-xs border border-gray-200 dark:border-gray-600 rounded-md disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300">Prev</button>
                <span class="text-xs text-gray-500 dark:text-gray-400">{{ versionPage }} / {{ totalVersionPages }}</span>
                <button @click="versionPage = Math.min(totalVersionPages, versionPage + 1)" :disabled="versionPage >= totalVersionPages"
                  class="px-3 py-1.5 text-xs border border-gray-200 dark:border-gray-600 rounded-md disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300">Next</button>
              </div>
            </div>
          </div>

          <!-- SBOMs Tab -->
          <div v-show="activeTab === 'sboms'">
            <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
              <div class="flex items-center gap-3 flex-wrap">
                <p class="text-sm text-gray-500 dark:text-gray-400">SBOMs for this product.</p>
                <select v-if="versions.length > 0" v-model="sbomVersionFilter" @change="fetchSBOMs(selectedProduct)" class="input text-sm">
                  <option value="">All versions</option>
                  <option v-for="v in versions" :key="v.id" :value="v.id">v{{ v.version }}</option>
                </select>
              </div>
              <button @click="triggerSBOMUpload" :disabled="isUploadingSBOM" class="btn-primary flex items-center text-sm">
                {{ isUploadingSBOM ? 'Uploading...' : 'Upload SBOM' }}
              </button>
            </div>
            <p v-if="!sbomVersionFilter && versions.length > 0" class="text-xs text-amber-600 dark:text-amber-400 mb-3">Select a specific version to upload an SBOM.</p>
            <input ref="sbomFileInput" type="file" accept=".json,.xml,.spdx,.cdx" class="hidden" @change="uploadSBOM" />

            <div v-if="isLoadingSBOMs" class="text-center py-8"><div class="animate-spin rounded-full h-8 w-8 border-2 border-gray-200 border-t-blue-500 mx-auto"></div></div>
            <div v-else-if="sboms.length === 0" class="text-center py-10 border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-lg">
              <p class="text-gray-500 dark:text-gray-400 text-sm">No SBOMs uploaded yet.</p>
              <p class="text-gray-400 dark:text-gray-500 text-xs mt-1">Supports CycloneDX and SPDX formats (.json, .xml)</p>
            </div>
            <template v-else>
            <div v-if="diffFromId" class="mb-3 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg flex items-center justify-between">
              <p class="text-sm text-blue-800 dark:text-blue-200">
                Comparison base selected. Click <span class="font-semibold">Compare</span> on another SBOM to see the diff.
              </p>
              <button @click="diffFromId = null" class="text-xs text-blue-700 dark:text-blue-300 underline">Cancel</button>
            </div>
            <div class="space-y-3">
              <div v-for="sbom in sboms" :key="sbom.id"
                class="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg border"
                :class="diffFromId === sbom.id ? 'border-blue-500 ring-1 ring-blue-500' : 'border-gray-200 dark:border-gray-600'">
                <div class="flex items-center gap-3 min-w-0 flex-1">
                  <span class="inline-flex items-center px-2 py-1 rounded text-xs font-semibold uppercase"
                    :class="sbom.format === 'cyclonedx' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' : 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'">
                    {{ sbom.format }}
                  </span>
                  <span v-if="versionLabel(sbom.version_id)" class="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold bg-gray-200 text-gray-700 dark:bg-gray-600 dark:text-gray-200">
                    v{{ versionLabel(sbom.version_id) }}
                  </span>
                  <div class="min-w-0 flex-1">
                    <p class="text-sm font-medium text-gray-900 dark:text-white truncate">{{ sbom.name }}</p>
                    <p class="text-xs text-gray-500 dark:text-gray-400">{{ sbom.spec_version }} &middot; {{ sbom.component_count }} components</p>
                  </div>
                </div>
                <div class="flex items-center gap-2 flex-shrink-0 ml-3">
                  <button @click.stop="handleDiffClick(sbom)" class="p-1.5 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 rounded"
                    :title="diffFromId && diffFromId !== sbom.id ? 'Compare against this SBOM' : 'Use as comparison base'">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"/></svg>
                  </button>
                  <button @click.stop="toggleSBOMVisibility(sbom)" class="p-1.5 rounded transition-colors" :title="sbom.is_public ? 'Visible on public trust center' : 'Hidden from public trust center'"
                    :class="sbom.is_public ? 'text-green-600 hover:text-green-700 dark:text-green-400' : 'text-gray-400 hover:text-gray-500'">
                    <svg v-if="sbom.is_public" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                    <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /></svg>
                  </button>
                  <button @click.stop="deleteSBOM(sbom)" class="p-1.5 text-gray-400 hover:text-red-600 dark:hover:text-red-400 rounded" title="Delete SBOM">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                  </button>
                </div>
              </div>
            </div>
            </template>
          </div>

          <!-- Scan Reports Tab -->
          <div v-show="activeTab === 'scans'">
            <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
              <div class="flex items-center gap-3 flex-wrap">
                <p class="text-sm text-gray-500 dark:text-gray-400">Vulnerability scan reports.</p>
                <select v-if="versions.length > 0" v-model="scanVersionFilter" @change="fetchScanReports(selectedProduct)" class="input text-sm">
                  <option value="">All versions</option>
                  <option v-for="v in versions" :key="v.id" :value="v.id">v{{ v.version }}</option>
                </select>
              </div>
              <button @click="triggerScanUpload" :disabled="isUploadingScan" class="btn-primary flex items-center text-sm">
                {{ isUploadingScan ? 'Uploading...' : 'Upload Report' }}
              </button>
            </div>
            <p v-if="!scanVersionFilter && versions.length > 0" class="text-xs text-amber-600 dark:text-amber-400 mb-3">Select a specific version to upload a scan report.</p>
            <input ref="scanFileInput" type="file" accept=".json,.sarif" class="hidden" @change="uploadScanReport" />

            <div v-if="scanReports.length === 0" class="text-center py-10 border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-lg">
              <p class="text-gray-500 dark:text-gray-400 text-sm">No scan reports uploaded yet.</p>
              <p class="text-gray-400 dark:text-gray-500 text-xs mt-1">SARIF files can auto-import vulnerabilities</p>
            </div>
            <div v-else class="space-y-3">
              <div v-for="report in scanReports" :key="report.id" class="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600">
                <div class="flex items-center gap-3 min-w-0 flex-1">
                  <span class="inline-flex items-center px-2 py-1 rounded text-xs font-semibold uppercase bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200">{{ report.format }}</span>
                  <span v-if="versionLabel(report.version_id)" class="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold bg-gray-200 text-gray-700 dark:bg-gray-600 dark:text-gray-200">
                    v{{ versionLabel(report.version_id) }}
                  </span>
                  <div class="min-w-0 flex-1">
                    <p class="text-sm font-medium text-gray-900 dark:text-white truncate">{{ report.file_name }}</p>
                    <p class="text-xs text-gray-500 dark:text-gray-400">{{ formatFileSize(report.file_size) }}</p>
                  </div>
                </div>
                <div class="flex items-center gap-2 flex-shrink-0 ml-3">
                  <button @click.stop="toggleScanReportVisibility(report)" class="p-1.5 rounded transition-colors" :title="report.is_public ? 'Visible on public trust center' : 'Hidden from public trust center'"
                    :class="report.is_public ? 'text-green-600 hover:text-green-700 dark:text-green-400' : 'text-gray-400 hover:text-gray-500'">
                    <svg v-if="report.is_public" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                    <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /></svg>
                  </button>
                  <button @click.stop="deleteScanReport(report)" class="p-1.5 text-gray-400 hover:text-red-600 dark:hover:text-red-400 rounded" title="Delete">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Declaration of Conformity Tab -->
          <div v-show="activeTab === 'doc'">
            <div v-if="!docData && !docLoading" class="text-center py-10 border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-lg">
              <p class="text-gray-700 dark:text-gray-300 text-sm font-medium mb-2">EU Declaration of Conformity</p>
              <p class="text-gray-500 dark:text-gray-400 text-xs mb-4">Generate a DoC per CRA Annex VI from your product data.</p>
              <button @click="generateDoC" class="btn-primary text-sm">Generate Declaration</button>
            </div>
            <div v-if="docLoading" class="text-center py-10 text-gray-500 dark:text-gray-400">Generating...</div>
            <div v-if="docData" class="doc-preview">
              <div class="flex flex-wrap justify-end gap-2 mb-4">
                <button @click="printDoC" class="px-4 py-2 text-sm font-medium bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors">Print / Save as PDF</button>
                <button @click="docData = null" class="px-4 py-2 text-sm font-medium border border-gray-200 dark:border-gray-600 rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">Close</button>
              </div>
              <div ref="docPrintArea" class="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-4 sm:p-8 space-y-5 sm:space-y-6 text-sm text-gray-800 dark:text-gray-200">
                <div class="text-center border-b border-gray-200 dark:border-gray-700 pb-6">
                  <h2 class="text-lg font-bold uppercase tracking-wide">EU Declaration of Conformity</h2>
                  <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">{{ docData.regulatory_ref }}</p>
                </div>
                <div>
                  <h3 class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-1">1. Manufacturer</h3>
                  <p class="font-semibold">{{ docData.manufacturer_name }}</p>
                  <p>{{ docData.manufacturer_address }}</p>
                  <p>{{ docData.manufacturer_contact }}</p>
                </div>
                <div>
                  <h3 class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-1">2. Product Identification</h3>
                  <p class="font-semibold">{{ docData.product_name }}</p>
                  <p v-if="docData.product_description">{{ docData.product_description }}</p>
                  <p v-if="docData.product_versions && docData.product_versions.length">Versions: {{ docData.product_versions.join(', ') }}</p>
                </div>
                <div v-if="docData.standards && docData.standards.length">
                  <h3 class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-1">3. Referenced Standards &amp; Frameworks</h3>
                  <ul class="list-disc ml-5 space-y-0.5">
                    <li v-for="s in docData.standards" :key="s">{{ s }}</li>
                  </ul>
                </div>
                <div v-if="docData.sbom_formats && docData.sbom_formats.length">
                  <h3 class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-1">4. SBOM Documentation</h3>
                  <p>Software Bill of Materials provided in: {{ docData.sbom_formats.join(', ') }}</p>
                </div>
                <div v-if="docData.support_end_dates && docData.support_end_dates.length">
                  <h3 class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-1">5. Support Lifecycle</h3>
                  <ul class="list-disc ml-5 space-y-0.5">
                    <li v-for="d in docData.support_end_dates" :key="d">{{ d }}</li>
                  </ul>
                </div>
                <div class="pt-4 border-t border-gray-200 dark:border-gray-700">
                  <h3 class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-2">Declaration</h3>
                  <p>{{ docData.declaration_text }}</p>
                </div>
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8 pt-6 border-t border-gray-200 dark:border-gray-700">
                  <div>
                    <p class="text-xs text-gray-500 dark:text-gray-400 mb-1">Place and date</p>
                    <p class="border-b border-gray-300 dark:border-gray-600 pb-1 min-h-[24px]">{{ docData.generated_at }}</p>
                  </div>
                  <div>
                    <p class="text-xs text-gray-500 dark:text-gray-400 mb-1">Signature</p>
                    <div class="border-b border-gray-300 dark:border-gray-600 pb-1 min-h-[24px]"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Version Add/Edit Modal -->
    <div v-if="showVersionModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]" @click.self="showVersionModal = false">
      <div class="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full">
        <div class="p-6">
          <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">{{ editingVersion ? 'Edit Version' : 'Add Version' }}</h3>
          <form @submit.prevent="handleVersionSubmit" class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Version</label>
              <input v-model="versionForm.version" type="text" required placeholder="e.g. 1.0.0" class="input mt-1" />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Support End Date</label>
              <input v-model="versionForm.support_end_date" type="date" class="input mt-1" />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Release Notes</label>
              <RichTextEditor v-model="versionForm.release_notes" placeholder="What's new in this version..." />
            </div>
            <div class="flex justify-end space-x-3">
              <button v-if="editingVersion" type="button" @click="deleteVersion" class="px-4 py-2 border border-red-300 dark:border-red-600 rounded-md text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20">Delete</button>
              <button type="button" @click="showVersionModal = false" class="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">Cancel</button>
              <button type="submit" :disabled="isSavingVersion" class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50">
                {{ isSavingVersion ? 'Saving...' : (editingVersion ? 'Save' : 'Add') }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <DeleteConfirmationModal
      :show="showDeleteModal"
      title="Confirm Delete Product"
      message="Are you sure you want to delete this product? All versions, SBOMs, and scan reports will also be deleted."
      :loading="isDeleting"
      @confirm="handleDeleteConfirm"
      @cancel="showDeleteModal = false"
    />

    <div v-if="diffResult" class="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" @click.self="diffResult = null">
      <div class="bg-white dark:bg-gray-800 rounded-lg max-w-4xl w-full max-h-[85vh] overflow-hidden flex flex-col">
        <div class="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div>
            <h3 class="text-lg font-semibold text-gray-900 dark:text-white">SBOM Diff</h3>
            <p class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {{ diffResult.summary.added }} added &middot; {{ diffResult.summary.removed }} removed &middot; {{ diffResult.summary.changed }} changed
            </p>
          </div>
          <button @click="diffResult = null" class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
          </button>
        </div>
        <div class="p-4 overflow-auto space-y-4 text-sm">
          <div v-if="diffResult.added && diffResult.added.length">
            <h4 class="font-semibold text-green-700 dark:text-green-400 mb-2">Added ({{ diffResult.added.length }})</h4>
            <ul class="space-y-1">
              <li v-for="(c, i) in diffResult.added" :key="'a'+i" class="font-mono text-xs text-gray-700 dark:text-gray-300">
                + {{ c.name }}@{{ c.new_version || '?' }}<span v-if="c.new_license" class="text-gray-500"> ({{ c.new_license }})</span>
              </li>
            </ul>
          </div>
          <div v-if="diffResult.removed && diffResult.removed.length">
            <h4 class="font-semibold text-red-700 dark:text-red-400 mb-2">Removed ({{ diffResult.removed.length }})</h4>
            <ul class="space-y-1">
              <li v-for="(c, i) in diffResult.removed" :key="'r'+i" class="font-mono text-xs text-gray-700 dark:text-gray-300">
                - {{ c.name }}@{{ c.old_version || '?' }}<span v-if="c.old_license" class="text-gray-500"> ({{ c.old_license }})</span>
              </li>
            </ul>
          </div>
          <div v-if="diffResult.changed && diffResult.changed.length">
            <h4 class="font-semibold text-amber-700 dark:text-amber-400 mb-2">Changed ({{ diffResult.changed.length }})</h4>
            <ul class="space-y-1">
              <li v-for="(c, i) in diffResult.changed" :key="'c'+i" class="font-mono text-xs text-gray-700 dark:text-gray-300">
                <span class="inline-block px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200 text-[10px] uppercase mr-1">{{ c.change.replace('_', ' ') }}</span>
                {{ c.name }}<span v-if="c.old_version || c.new_version">: {{ c.old_version }} &rarr; {{ c.new_version }}</span>
                <span v-if="c.old_license !== c.new_license && c.change === 'license_changed'" class="text-gray-500"> &middot; {{ c.old_license || '-' }} &rarr; {{ c.new_license || '-' }}</span>
              </li>
            </ul>
          </div>
          <div v-if="!diffResult.added.length && !diffResult.removed.length && !diffResult.changed.length"
            class="text-center py-8 text-gray-500 dark:text-gray-400">
            No differences between these SBOMs.
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch } from 'vue'
import { useStore } from 'vuex'
import axios from 'axios'
import DataTable from '@/components/DataTable.vue'
import DeleteConfirmationModal from '@/components/DeleteConfirmationModal.vue'
import RichTextEditor from '@/components/RichTextEditor.vue'

const getAPIURL = () => {
  const envUrl = process.env.VUE_APP_API_URL || 'http://127.0.0.1:10000'
  const cleanUrl = envUrl.replace(/\/$/, '')
  return cleanUrl.endsWith('/api/v1') ? cleanUrl : `${cleanUrl}/api/v1`
}

export default {
  name: 'ProductsView',
  components: { DataTable, DeleteConfirmationModal, RichTextEditor },
  setup() {
    const store = useStore()
    const showModal = ref(false)
    const showDeleteModal = ref(false)
    const showVersionModal = ref(false)
    const selectedProduct = ref(null)
    const productToDelete = ref(null)
    const isSaving = ref(false)
    const isDeleting = ref(false)
    const activeTab = ref('details')

    const versions = ref([])
    const versionMetrics = ref({})
    const sboms = ref([])
    const diffFromId = ref(null)
    const diffResult = ref(null)
    const scanReports = ref([])
    const isLoadingSBOMs = ref(false)
    const isUploadingSBOM = ref(false)
    const isUploadingScan = ref(false)
    const sbomFileInput = ref(null)
    const scanFileInput = ref(null)
    const sbomVersionFilter = ref('')
    const scanVersionFilter = ref('')

    const editingVersion = ref(null)
    const isSavingVersion = ref(false)
    const versionForm = ref({ version: '', support_end_date: '', release_notes: '' })

    const versionSearch = ref('')
    const versionPage = ref(1)
    const versionPageSize = 25

    const headers = computed(() => ({ Authorization: `Bearer ${store.state.token}` }))
    const tenantId = computed(() => store.state.currentTenantId)

    const emptyForm = () => ({ name: '', description: '', website: '', security_contact: '', sboms_public: false })
    const form = ref(emptyForm())

    const columns = [
      { key: 'name', label: 'Name', sortable: true },
      { key: 'description', label: 'Description', sortable: false }
    ]

    const modalTabs = computed(() => [
      { id: 'details', label: 'Details' },
      { id: 'versions', label: 'Versions', count: versions.value.length },
      { id: 'sboms', label: 'SBOMs', count: sboms.value.length },
      { id: 'scans', label: 'Scan Reports', count: scanReports.value.length },
      { id: 'doc', label: 'Declaration of Conformity' }
    ])

    const versionMap = computed(() => {
      const map = {}
      for (const v of versions.value) map[v.id] = v.version
      return map
    })

    const versionLabel = (vid) => vid ? (versionMap.value[vid] || null) : null

    const filteredVersions = computed(() => {
      if (!versionSearch.value) return versions.value
      const q = versionSearch.value.toLowerCase()
      return versions.value.filter(v => v.version.toLowerCase().includes(q))
    })

    const totalVersionPages = computed(() => Math.max(1, Math.ceil(filteredVersions.value.length / versionPageSize)))

    const paginatedVersions = computed(() => {
      const start = (versionPage.value - 1) * versionPageSize
      return filteredVersions.value.slice(start, start + versionPageSize)
    })

    watch(versionSearch, () => { versionPage.value = 1 })

    const truncate = (text, maxLength) => text && text.length > maxLength ? text.substring(0, maxLength) + '...' : (text || '')
    const stripHtml = (html) => html ? html.replace(/<[^>]*>/g, '') : ''
    const formatDate = (dateStr) => dateStr ? new Date(dateStr).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : ''
    const formatFileSize = (bytes) => {
      if (!bytes) return '0 B'
      const k = 1024
      const sizes = ['B', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i]
    }
    const extractError = (error, fallback) => error.response?.data?.error || error.response?.data?.message || fallback
    const notify = (type, message) => store.dispatch('notification/showNotification', { type, message, duration: type === 'error' ? 5000 : 3000 })

    const openAddProduct = () => {
      selectedProduct.value = null
      form.value = emptyForm()
      activeTab.value = 'details'
      versions.value = []
      sboms.value = []
      scanReports.value = []
      showModal.value = true
    }

    const showProductDetails = (product) => {
      selectedProduct.value = product
      form.value = {
        name: product.name,
        description: product.description || '',
        website: product.website || '',
        security_contact: product.security_contact || '',
        sboms_public: product.sboms_public ?? false
      }
      activeTab.value = 'details'
      versions.value = []
      sboms.value = []
      scanReports.value = []
      sbomVersionFilter.value = ''
      scanVersionFilter.value = ''
      versionSearch.value = ''
      versionPage.value = 1
      showModal.value = true
      fetchVersions(product)
      fetchSBOMs(product)
      fetchScanReports(product)
    }

    const closeModal = () => {
      showModal.value = false
      selectedProduct.value = null
      form.value = emptyForm()
      activeTab.value = 'details'
    }

    const refreshProducts = () => store.dispatch('products/fetchData', { tenantId: tenantId.value, page: 1, entriesPerPage: 10, search: '', orderBy: 'id', sortDirection: 'asc' })

    const handleSubmit = async () => {
      try {
        isSaving.value = true
        const data = { ...form.value }
        if (selectedProduct.value) {
          await store.dispatch('products/updateData', { tenantId: tenantId.value, id: selectedProduct.value.id, data })
        } else {
          await store.dispatch('products/createData', { tenantId: tenantId.value, data })
        }
        await refreshProducts()
        notify('success', 'Product ' + (selectedProduct.value ? 'updated' : 'created'))
        closeModal()
      } catch (error) {
        notify('error', extractError(error, 'Failed to save product'))
      } finally {
        isSaving.value = false
      }
    }

    const deleteProduct = (product) => { productToDelete.value = product; showDeleteModal.value = true }
    const handleDeleteConfirm = async () => {
      if (!productToDelete.value) return
      try {
        isDeleting.value = true
        await store.dispatch('products/deleteData', { tenantId: tenantId.value, id: productToDelete.value.id })
        await refreshProducts()
        notify('success', 'Product deleted')
        closeModal()
        showDeleteModal.value = false
      } catch (error) {
        notify('error', extractError(error, 'Failed to delete product'))
      } finally {
        isDeleting.value = false
      }
    }

    // --- Versions ---
    const fetchVersions = async (product) => {
      if (!product?.id) return
      try {
        const res = await axios.get(`${getAPIURL()}/admin/tenants/${tenantId.value}/products/${product.id}/versions`, { headers: headers.value, params: { per_page: 100 } })
        versions.value = Array.isArray(res.data) ? res.data : (res.data?.data || [])
      } catch { versions.value = [] }
      // Fire-and-forget: hydrate per-version severity chips. Admin-only endpoint;
      // 403/404 just leaves the chips off (graceful degradation for non-admins).
      try {
        const r = await axios.get(`${getAPIURL()}/admin/tenants/${tenantId.value}/products/${product.id}/version-metrics`, { headers: headers.value })
        const map = {}
        for (const v of (r.data?.versions || [])) map[v.version_id] = v
        versionMetrics.value = map
      } catch { versionMetrics.value = {} }
    }

    const openAddVersion = () => {
      editingVersion.value = null
      versionForm.value = { version: '', support_end_date: '', release_notes: '' }
      showVersionModal.value = true
    }

    const openVersionDetail = (ver) => {
      editingVersion.value = ver
      let endDate = ''
      if (ver.support_end_date) {
        const d = new Date(ver.support_end_date)
        if (!isNaN(d.getTime())) endDate = d.toISOString().split('T')[0]
      }
      versionForm.value = { version: ver.version, support_end_date: endDate, release_notes: ver.release_notes || '' }
      showVersionModal.value = true
    }

    const handleVersionSubmit = async () => {
      if (!selectedProduct.value) return
      try {
        isSavingVersion.value = true
        const data = {
          version: versionForm.value.version,
          release_notes: versionForm.value.release_notes || '',
          support_end_date: versionForm.value.support_end_date ? new Date(versionForm.value.support_end_date + 'T00:00:00Z').toISOString() : null
        }
        if (editingVersion.value) {
          await axios.put(`${getAPIURL()}/admin/tenants/${tenantId.value}/products/${selectedProduct.value.id}/versions/${editingVersion.value.id}`, data, { headers: headers.value })
        } else {
          data.product_id = selectedProduct.value.id
          await axios.post(`${getAPIURL()}/admin/tenants/${tenantId.value}/products/${selectedProduct.value.id}/versions`, data, { headers: headers.value })
        }
        await fetchVersions(selectedProduct.value)
        showVersionModal.value = false
        notify('success', 'Version ' + (editingVersion.value ? 'updated' : 'created'))
      } catch (error) {
        notify('error', extractError(error, 'Failed to save version'))
      } finally {
        isSavingVersion.value = false
      }
    }

    const deleteVersion = async () => {
      if (!editingVersion.value || !selectedProduct.value) return
      try {
        await axios.delete(`${getAPIURL()}/admin/tenants/${tenantId.value}/products/${selectedProduct.value.id}/versions/${editingVersion.value.id}`, { headers: headers.value })
        await fetchVersions(selectedProduct.value)
        showVersionModal.value = false
        notify('success', 'Version deleted')
      } catch (error) {
        notify('error', extractError(error, 'Failed to delete version'))
      }
    }

    // --- SBOMs ---
    const fetchSBOMs = async (product) => {
      if (!product?.id) return
      try {
        isLoadingSBOMs.value = true
        const params = {}
        if (sbomVersionFilter.value) params.version_id = sbomVersionFilter.value
        const res = await axios.get(`${getAPIURL()}/admin/tenants/${tenantId.value}/products/${product.id}/sboms`, { headers: headers.value, params })
        sboms.value = Array.isArray(res.data) ? res.data : (res.data?.data || [])
      } catch { sboms.value = [] }
      finally { isLoadingSBOMs.value = false }
    }

    const triggerSBOMUpload = () => {
      if (!sbomVersionFilter.value && versions.value.length > 0) {
        notify('error', 'Please select a specific version before uploading an SBOM')
        return
      }
      sbomFileInput.value?.click()
    }
    const uploadSBOM = async (event) => {
      const file = event.target.files[0]
      if (!file || !selectedProduct.value) return
      try {
        isUploadingSBOM.value = true
        const formData = new FormData()
        formData.append('file', file)
        if (sbomVersionFilter.value) formData.append('version_id', sbomVersionFilter.value)
        await axios.post(`${getAPIURL()}/admin/tenants/${tenantId.value}/products/${selectedProduct.value.id}/sboms`, formData, { headers: { ...headers.value, 'Content-Type': 'multipart/form-data' } })
        notify('success', 'SBOM uploaded')
        await fetchSBOMs(selectedProduct.value)
      } catch (error) {
        notify('error', extractError(error, 'Failed to upload SBOM'))
      } finally {
        isUploadingSBOM.value = false
        event.target.value = ''
      }
    }

    const toggleSBOMVisibility = async (sbom) => {
      if (!selectedProduct.value) return
      try {
        const res = await axios.put(`${getAPIURL()}/admin/tenants/${tenantId.value}/products/${selectedProduct.value.id}/sboms/${sbom.id}`, { is_public: !sbom.is_public }, { headers: headers.value })
        sbom.is_public = res.data.is_public
        notify('success', `SBOM ${sbom.is_public ? 'visible' : 'hidden'} on public trust center`)
      } catch (error) {
        notify('error', extractError(error, 'Failed to update visibility'))
      }
    }

    const handleDiffClick = async (sbom) => {
      if (!diffFromId.value) {
        diffFromId.value = sbom.id
        return
      }
      if (diffFromId.value === sbom.id) {
        diffFromId.value = null
        return
      }
      try {
        const res = await axios.get(`${getAPIURL()}/admin/tenants/${tenantId.value}/sboms/diff`, {
          headers: headers.value,
          params: { from: diffFromId.value, to: sbom.id }
        })
        diffResult.value = res.data
        diffFromId.value = null
      } catch (error) {
        notify('error', extractError(error, 'Failed to compute SBOM diff'))
      }
    }

    const deleteSBOM = async (sbom) => {
      if (!selectedProduct.value) return
      try {
        await axios.delete(`${getAPIURL()}/admin/tenants/${tenantId.value}/products/${selectedProduct.value.id}/sboms/${sbom.id}`, { headers: headers.value })
        notify('success', 'SBOM deleted')
        await fetchSBOMs(selectedProduct.value)
      } catch (error) {
        notify('error', extractError(error, 'Failed to delete SBOM'))
      }
    }

    // --- Scan Reports ---
    const fetchScanReports = async (product) => {
      if (!product?.id) return
      try {
        const params = {}
        if (scanVersionFilter.value) params.version_id = scanVersionFilter.value
        const res = await axios.get(`${getAPIURL()}/admin/tenants/${tenantId.value}/products/${product.id}/scan-reports`, { headers: headers.value, params })
        scanReports.value = Array.isArray(res.data) ? res.data : (res.data?.data || [])
      } catch { scanReports.value = [] }
    }

    const triggerScanUpload = () => {
      if (!scanVersionFilter.value && versions.value.length > 0) {
        notify('error', 'Please select a specific version before uploading a scan report')
        return
      }
      scanFileInput.value?.click()
    }
    const uploadScanReport = async (event) => {
      const file = event.target.files[0]
      if (!file || !selectedProduct.value) return
      try {
        isUploadingScan.value = true
        const formData = new FormData()
        formData.append('file', file)
        if (scanVersionFilter.value) formData.append('version_id', scanVersionFilter.value)
        const res = await axios.post(`${getAPIURL()}/admin/tenants/${tenantId.value}/products/${selectedProduct.value.id}/scan-reports?auto_import=true`, formData, { headers: { ...headers.value, 'Content-Type': 'multipart/form-data' } })
        const imported = res.data?.imported_vulns || 0
        notify('success', `Report uploaded${imported > 0 ? ` — ${imported} vulnerabilities imported` : ''}`)
        await fetchScanReports(selectedProduct.value)
      } catch (error) {
        notify('error', extractError(error, 'Failed to upload scan report'))
      } finally {
        isUploadingScan.value = false
        event.target.value = ''
      }
    }

    const toggleScanReportVisibility = async (report) => {
      if (!selectedProduct.value) return
      try {
        const res = await axios.put(`${getAPIURL()}/admin/tenants/${tenantId.value}/products/${selectedProduct.value.id}/scan-reports/${report.id}`, { is_public: !report.is_public }, { headers: headers.value })
        report.is_public = res.data.is_public
        notify('success', `Report ${report.is_public ? 'visible' : 'hidden'} on public trust center`)
      } catch (error) {
        notify('error', extractError(error, 'Failed to update visibility'))
      }
    }

    const deleteScanReport = async (report) => {
      if (!selectedProduct.value) return
      try {
        await axios.delete(`${getAPIURL()}/admin/tenants/${tenantId.value}/products/${selectedProduct.value.id}/scan-reports/${report.id}`, { headers: headers.value })
        notify('success', 'Report deleted')
        await fetchScanReports(selectedProduct.value)
      } catch (error) {
        notify('error', extractError(error, 'Failed to delete report'))
      }
    }

    const docData = ref(null)
    const docLoading = ref(false)
    const docPrintArea = ref(null)

    const generateDoC = async () => {
      if (!selectedProduct.value) return
      docLoading.value = true
      try {
        const res = await axios.get(`${getAPIURL()}/admin/tenants/${tenantId.value}/products/${selectedProduct.value.id}/declaration-of-conformity`, { headers: headers.value })
        docData.value = res.data
      } catch (error) {
        notify('error', extractError(error, 'Failed to generate Declaration of Conformity'))
      } finally {
        docLoading.value = false
      }
    }

    const printDoC = () => {
      const el = docPrintArea.value
      if (!el) return
      const win = window.open('', '_blank')
      win.document.write(`<!DOCTYPE html><html><head><title>Declaration of Conformity</title>
        <style>body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;padding:40px;color:#1a202c;font-size:14px;line-height:1.6}
        h2{font-size:18px;text-transform:uppercase;letter-spacing:2px;text-align:center;margin-bottom:8px}
        h3{font-size:11px;text-transform:uppercase;color:#718096;margin-bottom:4px;margin-top:24px}
        ul{margin-left:20px}li{margin-bottom:2px}.sig-grid{display:grid;grid-template-columns:1fr 1fr;gap:32px;margin-top:32px;padding-top:24px;border-top:1px solid #e2e8f0}
        .sig-label{font-size:11px;color:#718096;margin-bottom:4px}.sig-line{border-bottom:1px solid #a0aec0;min-height:24px;padding-bottom:4px}
        .border-b{border-bottom:1px solid #e2e8f0;padding-bottom:24px}.text-center{text-align:center}.text-xs{font-size:11px;color:#718096}
        @media print{body{padding:20px}}</style></head><body>`)
      win.document.write(el.innerHTML)
      win.document.write('</body></html>')
      win.document.close()
      win.print()
    }

    return {
      columns, showModal, showDeleteModal, showVersionModal, selectedProduct, productToDelete,
      form, isSaving, isDeleting, activeTab, modalTabs,
      versions, versionMetrics, sboms, scanReports, sbomVersionFilter, scanVersionFilter,
      isLoadingSBOMs, isUploadingSBOM, isUploadingScan,
      sbomFileInput, scanFileInput,
      editingVersion, isSavingVersion, versionForm,
      versionSearch, versionPage, versionPageSize, filteredVersions, paginatedVersions, totalVersionPages,
      openAddProduct, showProductDetails, closeModal, handleSubmit,
      deleteProduct, handleDeleteConfirm,
      fetchVersions, openAddVersion, openVersionDetail, handleVersionSubmit, deleteVersion,
      fetchSBOMs, triggerSBOMUpload, uploadSBOM, deleteSBOM, toggleSBOMVisibility,
      diffFromId, diffResult, handleDiffClick,
      fetchScanReports, triggerScanUpload, uploadScanReport, deleteScanReport, toggleScanReportVisibility,
      docData, docLoading, docPrintArea, generateDoC, printDoC,
      versionLabel, truncate, stripHtml, formatDate, formatFileSize
    }
  }
}
</script>
